import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FormInput from '../../components/elements/form_input';
import FileUpload from '../../components/elements/file_upload';
import EmailFirstAuth from '../../components/auth/EmailFirstAuth';
import {  Map } from '../../assets/icons';
import '../../assets/css/doctor/register.css'; // Reusing doctor styles
import { ahtApi } from '../../network/apis/aht';
import { saveDraft, loadDraft, clearDraft, getDraftAge } from '../../utils/registrationDraft';
import { Button } from 'react-bootstrap';

// Material Design color palette
const materialColors = {
    primary: '#1976D2',
    secondary: '#424242',
    success: '#4CAF50',
    error: '#F44336',
    warning: '#FF9800',
    info: '#2196F3',
    lightBg: '#F5F5F5',
    borderColor: '#E0E0E0',
    textPrimary: '#212121',
    textSecondary: '#757575'
};

const AHTRegister = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showDraftModal, setShowDraftModal] = useState(false);
    const [draftAge, setDraftAge] = useState(null);
    
    const [formData, setFormData] = useState({
        // Professional Details
        license_number: '',
        specialization: '',
        experience: '',
        qualifications: [],
        certifications: [],

        // Service Area Details
        coverage_area: '',
        coverage_address: '',
        longitude:'',
        latitude: '',
        working_hours_from: '',
        working_hours_to: '',
        consultation_fee: '',

        // Documents
        license_document: null,
        identity_document: null
    });

    // const validateSpecialization = (specialization) => {
    //     const lowerSpecialization = specialization.toLowerCase();
    //     return lowerSpecialization.includes('aquatic') || 
    //            lowerSpecialization.includes('fish') ||
    //            lowerSpecialization.includes('aqua') ||
    //            lowerSpecialization.includes('animal');
    // };

    const totalSteps = 4; // Account, Professional, Service Area, Documents

    // Check for existing draft on mount
    useEffect(() => {
        const draft = loadDraft('aht');
        if (draft && (draft.data.license_number || draft.data.coverage_area)) {
            setDraftAge(getDraftAge('aht'));
            setShowDraftModal(true);
        }
    }, []);
    
    // Auto-save when form data changes (only for non-auth steps)
    useEffect(() => {
        if (currentStep > 1 && (formData.license_number || formData.coverage_area)) {
            saveDraft('aht', formData, currentStep);
        }
    }, [formData, currentStep]);
    
    const handleRestoreDraft = () => {
        const draft = loadDraft('aht');
        if (draft) {
            setFormData(prev => ({ ...prev, ...draft.data }));
            // Only restore to step 2+ if authenticated
            if (isAuthenticated && draft.currentStep > 1) {
                setCurrentStep(draft.currentStep);
            }
            setSuccess('Draft restored successfully!');
        }
        setShowDraftModal(false);
    };
    
    const handleDiscardDraft = () => {
        clearDraft('aht');
        setShowDraftModal(false);
    };

    // Handle authentication completion from EmailFirstAuth component
    const handleAuthComplete = (userData) => {
        setIsAuthenticated(true);
        
        // Check if user is already registered as an AHT
        if (userData?.is_aht) {
            setSuccess('You are already registered as an AHT! Redirecting...');
            setTimeout(() => navigate('/aht'), 1500);
            return;
        }
        
        // Pre-fill any available user data
        if (userData) {
            setFormData(prev => ({
                ...prev,
                email: userData.email || '',
                firstName: userData.firstName || userData.first_name || '',
                lastName: userData.lastName || userData.last_name || '',
                phone: userData.phone || ''
            }));
        }
        
        // Move to next step
        setCurrentStep(2);
    };

    const geocodeTimeoutRef = useRef(null);
    const autocompleteServiceRef = useRef(null);
    const placesServiceRef = useRef(null);
    const googleScriptLoadedRef = useRef(false);
    
    // Add state for address lookup
    const [addressLookup, setAddressLookup] = useState({
        loading: false,
        options: [],
        error: null
    });

    // Load Google Maps JavaScript SDK
    useEffect(() => {
        const loadGoogleMapsScript = () => {
            if (window.google?.maps?.places) {
                autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
                placesServiceRef.current = new window.google.maps.places.PlacesService(
                    document.createElement('div')
                );
                googleScriptLoadedRef.current = true;
                return;
            }

            if (document.querySelector('script[src*="maps.googleapis.com"]')) {
                const checkGoogle = setInterval(() => {
                    if (window.google?.maps?.places) {
                        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
                        placesServiceRef.current = new window.google.maps.places.PlacesService(
                            document.createElement('div')
                        );
                        googleScriptLoadedRef.current = true;
                        clearInterval(checkGoogle);
                    }
                }, 100);
                return;
            }

            const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyCEBU9rIpanT0nUeCI3RWjKLyVmfKG7-x8';
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
                placesServiceRef.current = new window.google.maps.places.PlacesService(
                    document.createElement('div')
                );
                googleScriptLoadedRef.current = true;
            };
            document.head.appendChild(script);
        };

        loadGoogleMapsScript();
    }, []);

    // Geocode function using Google Places Autocomplete
    const geocodeAddress = async (address) => {
        if (!address.trim() || address.length < 3) {
            return;
        }
        
        setAddressLookup(prev => ({ ...prev, loading: true, error: null }));
        
        // Try Google Places first if loaded
        if (googleScriptLoadedRef.current && autocompleteServiceRef.current) {
            try {
                autocompleteServiceRef.current.getPlacePredictions(
                    {
                        input: address,
                        types: ['geocode', 'establishment']
                    },
                    (predictions, status) => {
                        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                            const detailPromises = predictions.slice(0, 5).map(prediction => {
                                return new Promise((resolve) => {
                                    placesServiceRef.current.getDetails(
                                        {
                                            placeId: prediction.place_id,
                                            fields: ['geometry', 'formatted_address']
                                        },
                                        (place, detailStatus) => {
                                            if (detailStatus === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
                                                resolve({
                                                    address: place.formatted_address || prediction.description,
                                                    lat: place.geometry.location.lat(),
                                                    lon: place.geometry.location.lng()
                                                });
                                            } else {
                                                resolve(null);
                                            }
                                        }
                                    );
                                });
                            });
                            
                            Promise.all(detailPromises).then(results => {
                                const validResults = results.filter(r => r !== null);
                                setAddressLookup(prev => ({
                                    ...prev,
                                    options: validResults,
                                    loading: false
                                }));
                            });
                        } else {
                            setAddressLookup(prev => ({
                                ...prev,
                                loading: false,
                                error: 'No results found'
                            }));
                        }
                    }
                );
            } catch (err) {
                setAddressLookup(prev => ({
                    ...prev,
                    loading: false,
                    error: err.message
                }));
            }
        }
    };

    const handleAddressChange = (value) => {
        setFormData({ ...formData, coverage_address: value });
        
        if (geocodeTimeoutRef.current) {
            clearTimeout(geocodeTimeoutRef.current);
        }
        
        geocodeTimeoutRef.current = setTimeout(() => {
            geocodeAddress(value);
        }, 500);
    };

    // Select an address from suggestions
    const selectAddress = (option) => {
        setFormData({
            ...formData,
            coverage_address: option.address,
            latitude: parseFloat(option.lat).toFixed(6),
            longitude: parseFloat(option.lon).toFixed(6)
        });
        setAddressLookup(prev => ({...prev, options: []}));
    };

    // Clear timeout on unmount
    useEffect(() => {
        return () => {
            if (geocodeTimeoutRef.current) {
                clearTimeout(geocodeTimeoutRef.current);
            }
        };
    }, []);

    // Handle file upload
    const handleFileUpload = (field, file) => {
        if (file === null) {
            setFormData(prev => ({
                ...prev,
                [field]: null
            }));
            return;
        }
        
        setFormData(prev => ({
            ...prev,
            [field]: file
        }));
    };

    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
        }
        setError(null);
        setSuccess(null);
        
        if (!formData.latitude || !formData.longitude) {
            setError('Please provide location coordinates. They should be automatically populated when you select an address.');
            setCurrentStep(3);
            return;
        }
        // Validate specialization
        // if (!validateSpecialization(formData.specialization)) {
        //     setError('Specialization must include "aquatic", "fish", "aqua", or "animal" terms. This platform is for aquatic health professionals.');
        //     setCurrentStep(2);
        //     return;
        // }

        // Check if files are selected
        if (!formData.license_document) {
            setError('Please upload your license document');
            setCurrentStep(4);
            return;
        }
        
        if (!formData.identity_document) {
            setError('Please upload your identity document');
            setCurrentStep(4);
            return;
        }
        
        setIsSubmitting(true);
        try {
            // Create FormData object for submission
            const formDataForSubmit = new FormData();
            
            // Append all text fields
            Object.keys(formData).forEach(key => {
                if (key !== 'license_document' && 
                    key !== 'identity_document' &&
                    formData[key] !== null &&
                    formData[key] !== undefined) {
                    formDataForSubmit.append(key, formData[key]);
                }
            });
            
            // Append files directly
            formDataForSubmit.append('license_document', formData.license_document);
            formDataForSubmit.append('identity_document', formData.identity_document);
            
            // Submit AHT registration data
            await ahtApi.newAHT(formDataForSubmit);
            
            // Clear draft on successful registration
            clearDraft('aht');
            
            setSuccess('Registration submitted successfully. Your application is under review.');
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            let errorMessage = 'Failed to submit registration';
            
            if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.response?.data?.details) {
                errorMessage = err.response.data.details;
            } else if (typeof err.response?.data === 'object') {
                const firstKey = Object.keys(err.response.data)[0];
                errorMessage = err.response.data[firstKey]?.[0] || errorMessage;
            }
            
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePrevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            setError(null);
        }
    };

    const handleNextStep = () => {
        setError(null);
        
        if (currentStep === 2) {
            // Validate professional details
            if (!formData.license_number || !formData.specialization || !formData.experience) {
                setError('Please fill in all professional details');
                return;
            }
            // if (!validateSpecialization(formData.specialization)) {
            //     setError('Specialization must include aquatic/animal health related terms');
            //     return;
            // }
        } else if (currentStep === 3) {
            // Validate service area details
            if (!formData.coverage_area || !formData.coverage_address || !formData.working_hours_from || 
                !formData.working_hours_to || !formData.consultation_fee) {
                setError('Please fill in all service area details');
                return;
            }
        }
        
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    };

    if (showDraftModal) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: materialColors.lightBg,
                padding: '16px'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '48px 32px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    maxWidth: '500px',
                    width: '100%',
                    boxShadow: '0 3px 5px -1px rgba(0,0,0,0.2), 0 6px 10px 0 rgba(0,0,0,0.14), 0 1px 18px 0 rgba(0,0,0,0.12)',
                    animation: 'fadeIn 0.3s ease-in-out'
                }}>
                    <h2 style={{
                        color: materialColors.textPrimary,
                        fontSize: '24px',
                        fontWeight: 500,
                        marginBottom: '8px',
                        marginTop: 0
                    }}>
                        Resume Previous Registration?
                    </h2>
                    <p style={{
                        color: materialColors.textSecondary,
                        fontSize: '14px',
                        marginTop: '8px',
                        marginBottom: '32px'
                    }}>
                        We found a draft of your registration from {draftAge} ago.
                    </p>
                    <div style={{ 
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <Button 
                            onClick={handleRestoreDraft}
                            label="Resume Registration"
                            style={{
                                backgroundColor: materialColors.success,
                                color: 'white',
                                padding: '10px 24px',
                                borderRadius: '4px',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                ':hover': {
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                                }
                            }}
                        >
                            Resume Registration
                        </Button>
                        <Button 
                            onClick={handleDiscardDraft}
                            label="Start Over"
                            style={{
                                backgroundColor: materialColors.error,
                                color: 'white',
                                padding: '10px 24px',
                                borderRadius: '4px',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                ':hover': {
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                                }
                            }}
                        >
                            Start Over
                            </Button>
                    </div>
                </div>
                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(-20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: materialColors.lightBg,
            padding: '32px 16px',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
        }}>
            <div style={{
                maxWidth: '600px',
                margin: '0 auto'
            }}>
                {/* Header Section */}
                <div style={{
                    textAlign: 'center',
                    marginBottom: '48px'
                }}>
                    <h1 style={{
                        color: materialColors.textPrimary,
                        fontSize: '32px',
                        fontWeight: 500,
                        marginBottom: '8px',
                        marginTop: 0
                    }}>
                        Animal Health Technologist Registration
                    </h1>
                    <p style={{
                        color: materialColors.textSecondary,
                        fontSize: '14px',
                        marginTop: '8px'
                    }}>
                        Complete your professional profile to get started
                    </p>
                </div>

                {/* Progress Stepper */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '48px',
                    position: 'relative'
                }}>
                    {/* Background line */}
                    <div style={{
                        position: 'absolute',
                        top: '20px',
                        left: '0',
                        right: '0',
                        height: '2px',
                        backgroundColor: materialColors.borderColor,
                        zIndex: 0
                    }} />
                    
                    {[1, 2, 3, 4].map(step => (
                        <div key={step} style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flex: 1,
                            position: 'relative',
                            zIndex: 1
                        }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: currentStep >= step ? materialColors.primary : materialColors.borderColor,
                                color: currentStep >= step ? 'white' : materialColors.textSecondary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 500,
                                marginBottom: '12px',
                                transition: 'all 0.3s ease',
                                boxShadow: currentStep >= step ? '0 4px 6px rgba(25, 118, 210, 0.2)' : 'none'
                            }}>
                                {step < currentStep ? '✓' : step}
                            </div>
                            <span style={{
                                fontSize: '12px',
                                color: currentStep >= step ? materialColors.primary : materialColors.textSecondary,
                                fontWeight: currentStep >= step ? 500 : 400,
                                textAlign: 'center',
                                transition: 'all 0.3s ease'
                            }}>
                                {step === 1 && 'Account'}
                                {step === 2 && 'Professional'}
                                {step === 3 && 'Service Area'}
                                {step === 4 && 'Documents'}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Alert Messages */}
                {error && (
                    <div style={{
                        backgroundColor: '#FFEBEE',
                        border: `1px solid ${materialColors.error}`,
                        borderRadius: '4px',
                        padding: '16px',
                        marginBottom: '24px',
                        color: '#C62828',
                        fontSize: '14px',
                        animation: 'slideDown 0.3s ease'
                    }}>
                        <strong style={{ display: 'block', marginBottom: '4px' }}>Error</strong>
                        {error}
                    </div>
                )}
                
                {success && (
                    <div style={{
                        backgroundColor: '#E8F5E9',
                        border: `1px solid ${materialColors.success}`,
                        borderRadius: '4px',
                        padding: '16px',
                        marginBottom: '24px',
                        color: '#2E7D32',
                        fontSize: '14px',
                        animation: 'slideDown 0.3s ease'
                    }}>
                        <strong style={{ display: 'block', marginBottom: '4px' }}>Success</strong>
                        {success}
                    </div>
                )}

                {/* Form Card */}
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    padding: '32px',
                    marginBottom: '24px',
                    animation: 'fadeInUp 0.3s ease'
                }}>
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        if (currentStep < totalSteps) {
                            handleNextStep();
                        } else {
                            handleSubmit();
                        }
                    }}>
                        {/* Step 1: Account */}
                        {currentStep === 1 && (
                            <div>
                                <h2 style={{
                                    color: materialColors.textPrimary,
                                    fontSize: '20px',
                                    fontWeight: 500,
                                    marginTop: 0,
                                    marginBottom: '24px'
                                }}>
                                    Create Your Account
                                </h2>
                                <p style={{
                                    color: materialColors.textSecondary,
                                    fontSize: '14px',
                                    marginBottom: '24px'
                                }}>
                                    Get started by creating your account. You'll use this to log in to your AHT profile.
                                </p>
                                <EmailFirstAuth onAuthSuccess={handleAuthComplete} />
                            </div>
                        )}

                        {/* Step 2: Professional Details */}
                        {currentStep === 2 && isAuthenticated && (
                            <div>
                                <h2 style={{
                                    color: materialColors.textPrimary,
                                    fontSize: '20px',
                                    fontWeight: 500,
                                    marginTop: 0,
                                    marginBottom: '24px'
                                }}>
                                    Professional Details
                                </h2>
                                <p style={{
                                    color: materialColors.textSecondary,
                                    fontSize: '14px',
                                    marginBottom: '24px'
                                }}>
                                    Tell us about your professional qualifications and experience.
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <FormInput
                                        label="License Number"
                                        type="text"
                                        placeholder="e.g., 001"
                                        value={formData.license_number}
                                        onChange={(e) => setFormData({...formData, license_number: e.target.value})}
                                        required
                                        showLabel
                                    />
                                    <FormInput
                                        label="Specialization"
                                        type="text"
                                        placeholder="e.g., Animal Health"
                                        value={formData.specialization}
                                        onChange={(e) => setFormData({...formData, specialization: e.target.value})}
                                        required
                                        showLabel
                                    />
                                    <FormInput
                                        label="Years of Experience"
                                        type="number"
                                        placeholder="e.g., 5"
                                        value={formData.experience}
                                        onChange={(e) => setFormData({...formData, experience: e.target.value})}
                                        required
                                        showLabel
                                    />
                                </div>
                            </div>
                        )}

                        {/* Step 3: Service Area Details */}
                        {currentStep === 3 && isAuthenticated && (
                            <div>
                                <h2 style={{
                                    color: materialColors.textPrimary,
                                    fontSize: '20px',
                                    fontWeight: 500,
                                    marginTop: 0,
                                    marginBottom: '24px'
                                }}>
                                    Service Area Details
                                </h2>
                                <p style={{
                                    color: materialColors.textSecondary,
                                    fontSize: '14px',
                                    marginBottom: '24px'
                                }}>
                                    Specify your coverage area and working hours.
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <FormInput
                                        label="Coverage Area (Farm/Facility Name)"
                                        type="text"
                                        placeholder="e.g., ABC Fish Farm"
                                        value={formData.coverage_area}
                                        onChange={(e) => setFormData({...formData, coverage_area: e.target.value})}
                                        required
                                        showLabel
                                    />
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            color: materialColors.textPrimary,
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            marginBottom: '8px'
                                        }}>
                                            Service Address
                                        </label>
                                        <div style={{ position: 'relative' }}>
                                            <FormInput
                                                label=""
                                                type="text"
                                                placeholder="Search for address..."
                                                value={formData.coverage_address}
                                                onChange={(e) => handleAddressChange(e.target.value)}
                                                required
                                                showLabel
                                            />
                                            <span style={{
                                                position: 'absolute',
                                                right: '12px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: materialColors.textSecondary,
                                                fontSize: '18px'
                                            }}>
                                                Longitude: {formData.longitude || 'N/A'} | Latitude: {formData.latitude || 'N/A'}
                                            </span>
                                            {addressLookup.loading && (
                                                <p style={{
                                                    fontSize: '12px',
                                                    color: materialColors.textSecondary,
                                                    marginTop: '8px'
                                                }}>
                                                    Loading suggestions...
                                                </p>
                                            )}
                                            {addressLookup.options.length > 0 && (
                                                <ul style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    right: 0,
                                                    backgroundColor: 'white',
                                                    border: `1px solid ${materialColors.borderColor}`,
                                                    borderTop: 'none',
                                                    borderRadius: '0 0 4px 4px',
                                                    maxHeight: '200px',
                                                    overflowY: 'auto',
                                                    listStyle: 'none',
                                                    padding: '8px 0',
                                                    margin: '0',
                                                    zIndex: 10,
                                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                                }}>
                                                    {addressLookup.options.map((option, idx) => (
                                                        <li
                                                            key={idx}
                                                            onClick={() => selectAddress(option)}
                                                            style={{
                                                                padding: '12px 16px',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                fontSize: '14px',
                                                                color: materialColors.textPrimary,
                                                                transition: 'background-color 0.2s ease',
                                                                backgroundColor: 'transparent'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.backgroundColor = materialColors.lightBg;
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                            }}
                                                        >
                                                            <img alt="Map" src={Map} style={{ marginRight: '12px', width: '18px', height: '18px' }} />
                                                            {option.address}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                    <FormInput
                                        label="Working Hours From"
                                        type="time"
                                        value={formData.working_hours_from}
                                        onChange={(e) => setFormData({...formData, working_hours_from: e.target.value})}
                                        required
                                        showLabel
                                    />
                                    <FormInput
                                        label="Working Hours To"
                                        type="time"
                                        value={formData.working_hours_to}
                                        onChange={(e) => setFormData({...formData, working_hours_to: e.target.value})}
                                        required
                                        showLabel
                                    />
                                    <FormInput
                                        label="Consultation Fee"
                                        type="number"
                                        placeholder="e.g., 50.00"
                                        value={formData.consultation_fee}
                                        onChange={(e) => setFormData({...formData, consultation_fee: e.target.value})}
                                        required
                                        showLabel
                                    />
                                </div>
                            </div>
                        )}

                        {/* Step 4: Documents */}
                        {currentStep === 4 && isAuthenticated && (
                            <div>
                                <h2 style={{
                                    color: materialColors.textPrimary,
                                    fontSize: '20px',
                                    fontWeight: 500,
                                    marginTop: 0,
                                    marginBottom: '24px'
                                }}>
                                    Upload Documents
                                </h2>
                                <p style={{
                                    color: materialColors.textSecondary,
                                    fontSize: '14px',
                                    marginBottom: '24px'
                                }}>
                                    Upload supporting documents for verification. Accepted formats: PDF, JPG, PNG (Max 15MB)
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <FileUpload
                                        label="License Document"
                                        onUpload={handleFileUpload}
                                        currentFile={formData.license_document}
                                        // onFileSelect={(file) => handleFileUpload('license_document', file)}
                                        acceptedFormats=".pdf,.jpg,.jpeg,.png"
                                        maxSize="15MB"
                                        field="license_document"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                    hint="Your veterinary license certificate (PDF, JPG, PNG - max 15MB)"
                                    disabled={isSubmitting}
                                        required
                                        showLabel
                                    />
                                    <FileUpload
                                        label="Identity Document"
                                        acceptedFormats=".pdf,.jpg,.jpeg,.png"
                                        onUpload={handleFileUpload}
                                        currentFile={formData.identity_document}
                                        // onFileSelect={(file) => handleFileUpload('identity_document', file)}
                                        field="identity_document"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                    hint="Government-issued ID (PDF, JPG, PNG - max 15MB)"
                                        maxSize="15MB"
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        {/* Navigation Buttons */}
                        {isAuthenticated && (
                            <div style={{
                                display: 'flex',
                                gap: '12px',
                                justifyContent: 'flex-end',
                                marginTop: '32px',
                                paddingTop: '24px',
                                borderTop: `1px solid ${materialColors.borderColor}`
                            }}>
                                {currentStep > 1 && (
                                    <Button
                                        type="button"
                                        label="Previous"
                                        onClick={handlePrevStep}
                                        style={{
                                            backgroundColor: 'transparent',
                                            color: materialColors.primary,
                                            border: `1px solid ${materialColors.primary}`,
                                            padding: '10px 24px',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            textTransform: 'uppercase',
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        Previous
                                    </Button>
                                )}
                                <Button
                                    type="submit"
                                    label={currentStep === totalSteps ? (isSubmitting ? 'Submitting...' : 'Submit Registration') : 'Next'}
                                    disabled={isSubmitting}
                                    style={{
                                        backgroundColor: currentStep === totalSteps ? materialColors.success : materialColors.primary,
                                        color: 'white',
                                        padding: '10px 32px',
                                        borderRadius: '4px',
                                        border: 'none',
                                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        textTransform: 'uppercase',
                                        transition: 'all 0.3s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        opacity: isSubmitting ? 0.7 : 1
                                    }}
                                >
                                    {currentStep === totalSteps ? (isSubmitting ? 'Submitting...' : 'Submit Registration') : 'Next'}
                                </Button>
                            </div>
                        )}
                    </form>
                </div>
            </div>

            {/* Global Styles */}
            <style>{`
                @keyframes slideDown {
                    from { 
                        opacity: 0; 
                        transform: translateY(-10px); 
                    }
                    to { 
                        opacity: 1; 
                        transform: translateY(0); 
                    }
                }
                
                @keyframes fadeInUp {
                    from { 
                        opacity: 0; 
                        transform: translateY(20px); 
                    }
                    to { 
                        opacity: 1; 
                        transform: translateY(0); 
                    }
                }
                
                @media (max-width: 600px) {
                    .form-card {
                        padding: 24px 16px;
                    }
                }
            `}</style>
        </div>
    );
};

export default AHTRegister;
